// Vertragsende (seit 26.10.2026): zwei Dokumente, mit denen EN:POWER einen
// Eigentümervertrag beendet, wenn sie ein Projekt nicht weiterverfolgt.
//
// - Aufhebungsvereinbarung: einvernehmlich, beide unterschreiben. Der erste Weg.
// - Kündigung: einseitig, nur EN:POWER unterschreibt (Brief per Einwurf-
//   Einschreiben). Der Weg, wenn der Eigentümer nicht mitmacht. Welche Klausel
//   trägt, hängt am Vertrag – KUENDIGUNGSGRUNDLAGEN.
//
// Welcher Vertrag beendet wird und von wann er ist, steht in keinem
// Bitrix-Feld. Beides trägt der Bearbeiter im Studio ein; die Vertragsart
// schlägt der Server aus dem Deal vor (dieselbe Entscheidung wie beim
// Vorvertrag, resolveContractKeyFromDeal). Die Werte gehen wie die
// Käuferfelder als fester Text in die Vorlage, nicht über das Mapping.

import { normalizePlaceholderName, replacePlaceholderWithManualValue } from './vertragsStand';

export const AUFHEBUNG_SCHLUESSEL = 'aufhebungsvereinbarung';
export const KUENDIGUNG_SCHLUESSEL = 'kuendigung-eigentuemervertrag';

export const AUFHEBUNG_PLATZHALTER = {
  vertrag: 'Aufzuhebender Vertrag',
  datum: 'Datum aufzuhebender Vertrag',
};

export const KUENDIGUNG_PLATZHALTER = {
  vertrag: 'Gekündigter Vertrag',
  datum: 'Datum gekündigter Vertrag',
  briefdatum: 'Briefdatum',
  betreff: 'Betreff Kündigung',
  erklaerung: 'Erklärung Kündigung',
};

// Die Eigentümerverträge, die beendet werden können. Im Vertragstext steht
// „der <Vertragsart> vom <Datum>“ – alle Namen sind männlich.
export const AUFZUHEBENDE_VERTRAEGE = [
  { key: 'pachtvertrag-dachverpachtung-ez', label: 'Pachtvertrag Dachverpachtung (Einmalzahlung)' },
  { key: 'pachtvertrag-dachverpachtung-jz', label: 'Pachtvertrag Dachverpachtung (Jahreszahlung)' },
  { key: 'dachnutzungsvertrag-sanierung', label: 'Dachnutzungsvertrag Sanierung' },
  { key: 'pachtvertrag-kleinspeicher-ez', label: 'Pachtvertrag Kleinspeicher (Einmalzahlung)' },
  { key: 'pachtvertrag-kleinspeicher-jz', label: 'Pachtvertrag Kleinspeicher (Jahreszahlung)' },
  { key: 'pachtvertrag-grossspeicher-ez', label: 'Pachtvertrag Großspeicher (Einmalzahlung)' },
  { key: 'pachtvertrag-grossspeicher-jz', label: 'Pachtvertrag Großspeicher (Jahreszahlung)' },
  { key: 'kaufvertrag-kleinspeicher', label: 'Kaufvertrag Kleinspeicher / Schwarmspeicher' },
];

const VORSORGLICH = 'Vorsorglich erklären wir zugleich die Kündigung zum nächstmöglichen Zeitpunkt.';

const KLEINSPEICHER = (termin) => [
  'Hiermit erklären wir gemäß § 11 Abs. 1 des Vertrags den Rücktritt vom Vertrag, da eine Wirtschaftlichkeit der Batteriespeicheranlage nicht mehr gegeben ist. Der Rücktritt wird mit Zugang dieses Schreibens wirksam.',
  `Hilfsweise kündigen wir den Vertrag gemäß § 11 Abs. 2 mit einer Frist von drei Monaten zum Monatsende, da der Grundbesitz aufgrund einer Änderung unserer Planung nicht mehr benötigt wird, nach unserer Berechnung zum ${termin}.`,
].join(' ');

const DACH = [
  'Hiermit erklären wir gemäß § 6 Abs. 2 des Vertrags den Rücktritt vom Vertrag. Mit der Installation der Photovoltaik-Anlage wurde nicht begonnen; aufgrund äußerer, von uns nicht zu vertretender Einflüsse ist eine wirtschaftliche Realisierung des Projekts nicht mehr gegeben. Der Rücktritt wird mit Zugang dieses Schreibens wirksam.',
  'Hilfsweise erklären wir den Rücktritt bzw. die Kündigung gemäß § 6 Abs. 1 des Vertrags, soweit dessen Voraussetzungen vorliegen.',
].join(' ');

const GROSSSPEICHER = (termin) => `Hiermit kündigen wir den Vertrag gemäß § 9 Abs. 3 mit einer Frist von drei Monaten zum Monatsende, da der vertragsgegenständliche Grundbesitz aufgrund einer Änderung der Planung nicht mehr benötigt wird. Nach unserer Berechnung endet der Vertrag damit zum ${termin}, andernfalls zum nächstzulässigen Termin.`;

/**
 * Rechtsgrundlage je Vertrag (Vorlagen geprüft am 26.10.2026). `art` steht im
 * Betreff; `text(termin)` ist die Erklärung. Kleinspeicher § 11 Abs. 5
 * verlangt das Einwurf-Einschreiben – der Brief trägt den Vermerk für alle.
 */
export const KUENDIGUNGSGRUNDLAGEN = {
  'pachtvertrag-kleinspeicher-ez': { art: 'Rücktritt', text: KLEINSPEICHER },
  'pachtvertrag-kleinspeicher-jz': { art: 'Rücktritt', text: KLEINSPEICHER },
  'pachtvertrag-dachverpachtung-ez': { art: 'Rücktritt', text: () => DACH },
  'pachtvertrag-dachverpachtung-jz': { art: 'Rücktritt', text: () => DACH },
  'pachtvertrag-grossspeicher-ez': { art: 'Kündigung', text: GROSSSPEICHER },
  'pachtvertrag-grossspeicher-jz': { art: 'Kündigung', text: GROSSSPEICHER },
  'dachnutzungsvertrag-sanierung': {
    art: 'Rücktritt',
    text: () => 'Hiermit erklären wir gemäß § 10 Abs. 1 in Verbindung mit § 2 des Vertrags den Rücktritt vom Vertrag. Mit dem Bau wurde nicht begonnen; wir verfolgen das Projekt nicht weiter. Der Rücktritt wird mit Zugang dieses Schreibens wirksam.',
  },
  'kaufvertrag-kleinspeicher': {
    art: 'Rücktritt',
    text: () => 'Hiermit erklären wir gemäß § 7 Abs. 2 des Vertrags den Rücktritt vom Vertrag, da eine Wirtschaftlichkeit der Batteriespeicheranlage nicht mehr gegeben ist und das Projekt nicht sinnvoll umgesetzt werden kann. Mit dem Bau wurde nicht begonnen. Der Rücktritt wird mit Zugang dieses Schreibens wirksam.',
  },
};

export const LEERE_AUFHEBUNG = { vertrag: '', datum: '', briefdatum: '' };

// Bis ein Einwurf-Einschreiben zugeht, rechnen wir drei Tage ab Briefdatum.
const ZUGANG_TAGE = 3;

export function istAufhebung(vertragKey) {
  return String(vertragKey || '').trim() === AUFHEBUNG_SCHLUESSEL;
}

export function istKuendigung(vertragKey) {
  return String(vertragKey || '').trim() === KUENDIGUNG_SCHLUESSEL;
}

/** Aufhebung oder Kündigung – beide brauchen die Angaben zum beendeten Vertrag. */
export function istVertragsende(vertragKey) {
  return istAufhebung(vertragKey) || istKuendigung(vertragKey);
}

/** Name zur Vertragsart aus dem Vorschlag des Servers, sonst ''. */
export function vertragsName(vertragKey) {
  const key = String(vertragKey || '').trim();
  return AUFZUHEBENDE_VERTRAEGE.find((v) => v.key === key)?.label || '';
}

function vertragsKeyZuName(name) {
  const t = String(name || '').trim();
  return AUFZUHEBENDE_VERTRAEGE.find((v) => v.label === t)?.key || '';
}

/** „2026-03-14“ (Datumsfeld) → „14.03.2026“; anderes bleibt, wie es ist. */
export function datumText(wert) {
  const t = String(wert || '').trim();
  const m = t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : t;
}

export function heuteIso(jetzt = new Date()) {
  const zwei = (n) => String(n).padStart(2, '0');
  return `${jetzt.getFullYear()}-${zwei(jetzt.getMonth() + 1)}-${zwei(jetzt.getDate())}`;
}

/**
 * Drei Monate zum Monatsende, gerechnet ab Zugang (Briefdatum + 3 Tage):
 * Zugang 29.10. → 29.01. → 31.01.; Zugang 01.11. → 01.02. → 28./29.02.
 */
export function kuendigungstermin(briefdatumIso) {
  const m = String(briefdatumIso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  const zugang = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + ZUGANG_TAGE);
  const ende = new Date(zugang.getFullYear(), zugang.getMonth() + 4, 0);
  return datumText(heuteIso(ende));
}

export function aufhebungVollstaendig(angaben) {
  return Boolean(String(angaben?.vertrag || '').trim() && String(angaben?.datum || '').trim());
}

/** Kündigung: nur mit bekannter Vertragsart – sonst gäbe es keine Klausel. */
export function kuendigungVollstaendig(angaben) {
  return aufhebungVollstaendig(angaben)
    && Boolean(KUENDIGUNGSGRUNDLAGEN[vertragsKeyZuName(angaben?.vertrag)])
    && Boolean(String(angaben?.briefdatum || '').trim());
}

export function vertragsendeVollstaendig(vertragKey, angaben) {
  if (istKuendigung(vertragKey)) return kuendigungVollstaendig(angaben);
  return aufhebungVollstaendig(angaben);
}

/** Was in der Aufhebungsvereinbarung ersetzt wird. Leere Angaben ersetzen nichts. */
export function aufhebungsFelder(angaben) {
  return {
    [AUFHEBUNG_PLATZHALTER.vertrag]: String(angaben?.vertrag || '').trim(),
    [AUFHEBUNG_PLATZHALTER.datum]: datumText(angaben?.datum),
  };
}

/** Was in der Kündigung ersetzt wird. Ohne bekannte Vertragsart keine Erklärung. */
export function kuendigungsFelder(angaben) {
  const vertrag = String(angaben?.vertrag || '').trim();
  const datum = datumText(angaben?.datum);
  const grundlage = KUENDIGUNGSGRUNDLAGEN[vertragsKeyZuName(vertrag)];
  const termin = kuendigungstermin(angaben?.briefdatum);
  return {
    [KUENDIGUNG_PLATZHALTER.vertrag]: vertrag,
    [KUENDIGUNG_PLATZHALTER.datum]: datum,
    [KUENDIGUNG_PLATZHALTER.briefdatum]: datumText(angaben?.briefdatum),
    [KUENDIGUNG_PLATZHALTER.betreff]: grundlage && vertrag && datum ? `${grundlage.art}: ${vertrag} vom ${datum}` : '',
    [KUENDIGUNG_PLATZHALTER.erklaerung]: grundlage && termin ? `${grundlage.text(termin)} ${VORSORGLICH}` : '',
  };
}

function ersetze(html, felder) {
  return Object.entries(felder).reduce(
    (stand, [platzhalter, wert]) => replacePlaceholderWithManualValue(stand, platzhalter, wert),
    html
  );
}

export function wendeAufhebungAn(html, angaben, aktiv = true) {
  if (!aktiv || typeof html !== 'string') return html;
  return ersetze(html, aufhebungsFelder(angaben));
}

/** Setzt die Angaben ein, die zur Vertragsart passen; andere Verträge bleiben unberührt. */
export function wendeVertragsendeAn(html, vertragKey, angaben) {
  if (typeof html !== 'string') return html;
  if (istAufhebung(vertragKey)) return ersetze(html, aufhebungsFelder(angaben));
  if (istKuendigung(vertragKey)) return ersetze(html, kuendigungsFelder(angaben));
  return html;
}

/** Diese Platzhalter gehören nicht ins Mapping – sie kommen aus dem Studio. */
export function istAufhebungsfeld(platzhalter) {
  const name = normalizePlaceholderName(platzhalter);
  return [...Object.values(AUFHEBUNG_PLATZHALTER), ...Object.values(KUENDIGUNG_PLATZHALTER)].includes(name);
}
